/**
 * **Mock only** — simulates the catalogue order the Session Service will return
 * from `GET …/questionnaire` per `packageType`. Production composes templates
 * in API response order via `composeEstateFlowTemplateFromDownloads` and does
 * not import this module.
 *
 * Each slug maps to `estate-client-schemas/templates/{slug}-template.json`
 * (`rules` maps to `templates/rules.json`, no `-template` suffix). `modals`
 * and `rules` are always merged separately — neither is a navigable section.
 */
import type {EstatePackageType} from './types';

export type EstateTemplateSlug =
  | 'about-you'
  | 'family'
  | 'guardians'
  | 'assets'
  | 'beneficiaries'
  | 'trust'
  | 'executor-and-poa'
  | 'health'
  | 'spouse-executor-and-poa'
  | 'spouse-health'
  | 'modals'
  | 'rules';

/**
 * Maps each catalogue template slug to the `Section.id` authored in that
 * template file — used to order/filter the `sectionReview` registry per package.
 */
export const ESTATE_SECTION_SLUG_TO_ID: Readonly<Record<Exclude<EstateTemplateSlug, 'modals' | 'rules'>, string>> = {
  'about-you': 'section_getting_started',
  family: 'section_family',
  guardians: 'section_guardians',
  assets: 'section_assets',
  beneficiaries: 'section_beneficiaries',
  trust: 'section_trust',
  'executor-and-poa': 'section_pour_over_will',
  health: 'section_health',
  'spouse-executor-and-poa': 'section_spouse_pour_over_will',
  'spouse-health': 'section_spouse_health',
};

/** Ordered `Section.id` values for a package plan (excludes `modals`). */
export function getPackageSectionIds(packageType: EstatePackageType): string[] {
  return getSectionPlanForPackageType(packageType)
    .map((slug) => ESTATE_SECTION_SLUG_TO_ID[slug as Exclude<EstateTemplateSlug, 'modals' | 'rules'>])
    .filter(Boolean);
}

/** Section templates included in a Will package, in API response order. */
export const WILL_SECTION_PLAN: readonly EstateTemplateSlug[] = [
  'about-you',
  'family',
  'guardians',
  'assets',
  'beneficiaries',
  'executor-and-poa',
  'health',
] as const;

/** Section templates included in a Trust package, in API response order. */
export const TRUST_SECTION_PLAN: readonly EstateTemplateSlug[] = [
  'about-you',
  'family',
  'guardians',
  'assets',
  'beneficiaries',
  'trust',
  'executor-and-poa',
  'health',
  'spouse-executor-and-poa',
  'spouse-health',
] as const;

export function getSectionPlanForPackageType(packageType: EstatePackageType): readonly EstateTemplateSlug[] {
  switch (packageType) {
    case 'TRUST':
      return TRUST_SECTION_PLAN;
    case 'WILL':
      return WILL_SECTION_PLAN;
    case 'ESTATE_INVENTORY':
      return [];
    default: {
      const _exhaustive: never = packageType;
      return _exhaustive;
    }
  }
}

/** Slugs required to compose a package flow (section plan + shared modals + shared rules). */
export function getTemplateSlugsForPackageType(packageType: EstatePackageType): readonly EstateTemplateSlug[] {
  const sectionSlugs = getSectionPlanForPackageType(packageType);
  return sectionSlugs.length > 0 ? [...sectionSlugs, 'modals', 'rules'] : sectionSlugs;
}
